// /bench: the phone at the bench, chrome-free. My block now and next (target,
// SOP, expert, station, Start); submit with the end-of-task survey, then a
// celebration and my personal best. Also: finished early (where spare time
// helps most), "This plan doesn't work" (a planner bug, never a silent
// override) and "I did some production work" (off the plan, for anyone).
import { IonContent, IonPage, useIonToast } from "@ionic/react";
import { useState } from "react";
import type { BenchView, Half, LogSessionRequest, PersonRow, SessionRow, TaskRow } from "../lib/types";
import { Celebration, PersonalBest, PreviewBanner, TaskChip, isPersonalBest } from "../design";
import { announce, can, useApi, useMe } from "../api/live";
import { ApiError, post } from "../api/client";
import { paths } from "../api/paths";
import { fixtureParam } from "../api/mode";
import { timeOf, weekdayLong } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { ErrorNote, Loading } from "../shell/ui";

const HOW_FAR = ["Finished", "Most of it", "About half", "Just started"];
const SMOOTH = [{ v: 3, t: "Yes" }, { v: 2, t: "Mostly" }, { v: 1, t: "No" }];

export function BenchPage() {
  const { me } = useMe();
  const supervisor = can(me, "supervisor");
  const q = new URLSearchParams(window.location.search);
  const [person, setPerson] = useState<string | null>(q.get("person") ?? fixtureParam("person"));
  const bench = useApi<BenchView>(paths.bench(person), ["plan", "session", "stock"]);
  const people = useApi<PersonRow[]>(supervisor ? paths.people() : null, ["person"]);
  const { byCode, tasks } = useTasks();
  const [panel, setPanel] = useState<null | "early" | "bug" | "other">(null);
  const [done, setDone] = useState<{ session: SessionRow; best: BenchView["personalBest"] } | null>(null);
  const [party, setParty] = useState(false);
  const v = bench.data;

  const half = v?.now?.half === "AM" ? "morning" : "afternoon";
  return (
    <IonPage>
      <IonContent>
        <PreviewBanner preview={me?.preview} />
        <div className="bench">
          <div className="bench__head">
            <div>
              <h1>{v?.person ?? me?.person?.name ?? "Bench"}</h1>
              {v?.now ? <span className="bench__when">{weekdayLong(v.now.day)} {half}</span> : null}
            </div>
            {supervisor && people.data ? (
              <select className="select select--inline" aria-label="Whose bench" value={v?.person ?? ""} onChange={(e) => setPerson(e.target.value)}>
                {people.data.filter((p) => p.plannable && p.active).map((p) => <option key={p.id}>{p.name}</option>)}
              </select>
            ) : null}
          </div>

          {!v ? (bench.error ? <ErrorNote error={bench.error} retry={bench.reload} /> : <Loading what="Finding your job" />) : (
            <>
              {done ? (
                <DoneCard done={done} task={byCode[done.session.task ?? ""]} onAgain={() => setDone(null)} />
              ) : v.current ? (
                <NowCard v={v} task={byCode[v.current.task ?? ""]} people={(people.data ?? []).map((p) => p.name)} onLogged={(s) => {
                  setDone({ session: s, best: v.personalBest });
                  setParty(true);
                  announce(["session"]);
                  bench.reload();
                }} />
              ) : (
                <section className="job"><div className="job__status"><span className="job__dot" />Now</div>
                  <p className="job__title">Nothing planned for you this {half}</p>
                  <p className="small muted" style={{ marginTop: "var(--space-2)" }}>If you did production work anyway, log it below.</p></section>
              )}

              {v.next ? (
                <section className="job" aria-label="Next">
                  <div className="job__status"><span className="job__dot" />Next · {weekdayLong(v.next.day)} {v.next.half === "AM" ? "morning" : "afternoon"}</div>
                  {v.next.kind === "task" ? (
                    <><TaskChip task={v.next.task ?? null} name={byCode[v.next.task ?? ""]?.name} group={byCode[v.next.task ?? ""]?.group} learner={v.next.learner} halfOnly={byCode[v.next.task ?? ""]?.halfOnly} size="lg" />
                      <p className="small muted" style={{ marginTop: "var(--space-2)" }}>Target {v.next.target}{v.next.station ? ` · ${v.next.station}` : ""}{v.next.expert ? ` · with ${v.next.expert}` : ""}</p></>
                  ) : <p className="job__title job__title--muted">{v.next.kind === "float" ? "Float: help where the shelves need it" : "Not in"}</p>}
                </section>
              ) : null}

              <div className="job__actions">
                <button type="button" className="btn btn--block" aria-expanded={panel === "early"} onClick={() => setPanel(panel === "early" ? null : "early")}>Finished early?</button>
                <button type="button" className="btn btn--block" aria-expanded={panel === "other"} onClick={() => setPanel(panel === "other" ? null : "other")}>I did some production work</button>
                <button type="button" className="btn btn--block btn--ghost" aria-expanded={panel === "bug"} onClick={() => setPanel(panel === "bug" ? null : "bug")}>This plan doesn't work</button>
              </div>
              {panel === "early" ? <SpareTime v={v} byCode={byCode} /> : null}
              {panel === "bug" ? <PlanBug v={v} onDone={() => setPanel(null)} /> : null}
              {panel === "other" ? <OtherWork v={v} tasks={tasks} onDone={(s) => { setPanel(null); setDone({ session: s, best: null }); setParty(true); announce(["session"]); }} /> : null}
            </>
          )}
        </div>
        <Celebration show={party} message={done ? `${done.session.actualQty ?? 0} done` : "Logged"} detail="Thanks: that's on the board now" onDone={() => setParty(false)} />
      </IonContent>
    </IonPage>
  );
}

function NowCard({ v, task, people, onLogged }: { v: BenchView; task?: TaskRow; people: string[]; onLogged: (s: SessionRow) => void }) {
  const cur = v.current!;
  const [present] = useIonToast();
  const [started, setStarted] = useState(cur.startedAt);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState<string>("");
  const [howFar, setHowFar] = useState<string | null>(null);
  const [smooth, setSmooth] = useState<number | null>(null);
  const [issue, setIssue] = useState("");
  const [enjoy, setEnjoy] = useState<number | null>(null);
  const [helpers, setHelpers] = useState<{ person: string; minutes: string }[]>([]);
  const [other, setOther] = useState("");
  const logged = cur.logged;

  const start = async () => {
    setBusy(true);
    try {
      const r = await post<{ startedAt?: string }>(paths.startSession(), { day: cur.day, half: cur.half, task: cur.task ?? null, person: v.person ?? undefined });
      setStarted(r?.startedAt ?? new Date().toISOString());
    } catch (e) { present({ message: (e as ApiError).message, duration: 3000, color: "danger" }); }
    setBusy(false);
  };

  const n = qty === "" ? null : Number(qty);
  const valid = n != null && Number.isInteger(n) && n >= 0;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    const body: LogSessionRequest = {
      day: cur.day, half: cur.half, kind: cur.kind === "float" ? "flexible" : "planned", task: cur.task ?? null, actualQty: n!,
      howFar, smoothness: smooth, enjoyment: enjoy, issue: smooth != null && smooth < 3 && issue.trim() ? issue.trim() : null,
      note: other.trim() ? `Other work: ${other.trim()}` : null,
      helpers: helpers.filter((h) => h.person).map((h) => ({ person: h.person, minutes: h.minutes === "" ? null : Number(h.minutes), qty: null })),
      person: v.person ?? undefined
    };
    try { onLogged(await post<SessionRow>(paths.sessions(), body)); }
    catch (e) { present({ message: (e as ApiError).message || "That didn't save. Try again.", duration: 4000, color: "danger" }); }
    setBusy(false);
  };

  return (
    <section className="job job--now" aria-label="Now">
      <div className="job__status"><span className="job__dot" />Now{cur.station ? ` · ${cur.station}` : ""}</div>
      {cur.kind === "task" ? (
        <>
          <TaskChip task={cur.task ?? null} name={task?.name} group={task?.group} learner={cur.learner} halfOnly={task?.halfOnly} size="lg" />
          <div className="job__target"><span className="job__target-n">{cur.target}</span><span className="job__target-k">to make{cur.learner ? " (a learner's target)" : ""}</span></div>
          <div className="job__meta">
            {cur.expert ? <div className="kv"><span className="kv__k">Expert</span><span className="kv__v">{cur.expert}</span></div> : null}
            {cur.station ? <div className="kv"><span className="kv__k">Station</span><span className="kv__v">{cur.station}</span></div> : null}
            {cur.sop ? <div className="kv"><span className="kv__k">SOP</span><span className="kv__v">{cur.sop}</span></div> : null}
            {task?.cure ? <div className="kv"><span className="kv__k">Cure</span><span className="kv__v">{task.cure}</span></div> : null}
          </div>
          {cur.steps.length ? <ol className="job__steps">{cur.steps.map((s) => <li key={s}>{s}</li>)}</ol> : null}
        </>
      ) : <p className="job__title">{cur.kind === "float" ? "Float: no set count" : "Not on the plan"}</p>}

      {logged ? (
        <p className="small" style={{ marginTop: "var(--space-4)" }}>Logged: {logged.actualQty} made{logged.plannedQty ? ` of ${logged.plannedQty}` : ""}. Logged sessions are locked.</p>
      ) : (
        <>
          <div className="job__start">
            {started ? <span>Started <b>{timeOf(started)}</b></span> : <button type="button" className="btn btn--primary" onClick={start} disabled={busy}>Start</button>}
          </div>
          <div className="survey">
            <h2>When you're done</h2>
            <label className="field"><span className="field__label">How many did you make?</span>
              <div className="stepper">
                <button type="button" className="btn" aria-label="One fewer" onClick={() => setQty(String(Math.max(0, (n ?? 0) - 1)))}>−</button>
                <input className="input" inputMode="numeric" pattern="[0-9]*" value={qty} placeholder={String(cur.target)} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} aria-label="Quantity" />
                <button type="button" className="btn" aria-label="One more" onClick={() => setQty(String((n ?? 0) + 1))}>+</button>
              </div>
            </label>
            <div className="field"><span className="field__label">How far did you get?</span>
              <div className="choices">{HOW_FAR.map((h) => <button key={h} type="button" className="choice" aria-pressed={howFar === h} onClick={() => setHowFar(h)}>{h}</button>)}</div>
            </div>
            <div className="field"><span className="field__label">Did it go smoothly?</span>
              <div className="choices">{SMOOTH.map((s) => <button key={s.v} type="button" className="choice" aria-pressed={smooth === s.v} onClick={() => setSmooth(s.v)}>{s.t}</button>)}</div>
            </div>
            {smooth != null && smooth < 3 ? (
              <label className="field"><span className="field__label">What got in the way?</span>
                <textarea className="textarea" value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="Ran out of front covers at 11:00" /></label>
            ) : null}
            <div className="field"><span className="field__label">How much did you enjoy it? (1–5)</span>
              <div className="choices">{[1, 2, 3, 4, 5].map((e) => <button key={e} type="button" className="choice" aria-pressed={enjoy === e} onClick={() => setEnjoy(e)}>{e}</button>)}</div>
            </div>
            <div className="field"><span className="field__label">Did anyone help?</span>
              {helpers.map((h, i) => (
                <div key={i} className="helper">
                  <select className="select" aria-label="Helper" value={h.person} onChange={(e) => setHelpers(helpers.map((x, j) => (j === i ? { ...x, person: e.target.value } : x)))}>
                    <option value="">Who?</option>{people.filter((p) => p !== v.person).map((p) => <option key={p}>{p}</option>)}
                  </select>
                  <input className="input" inputMode="numeric" placeholder="min" aria-label="Minutes" value={h.minutes} onChange={(e) => setHelpers(helpers.map((x, j) => (j === i ? { ...x, minutes: e.target.value.replace(/\D/g, "") } : x)))} />
                  <button type="button" className="btn" aria-label="Remove helper" onClick={() => setHelpers(helpers.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => setHelpers([...helpers, { person: "", minutes: "" }])}>Add a helper</button>
            </div>
            <label className="field"><span className="field__label">Any other work? (optional)</span>
              <textarea className="textarea" value={other} onChange={(e) => setOther(e.target.value)} placeholder="Taped 40 cameras while the glue cured" /></label>
            <button type="button" className="btn btn--primary btn--lg" disabled={!valid || busy} onClick={submit}>{busy ? "Saving…" : "Submit"}</button>
            {qty !== "" && !valid ? <p className="small muted">How many is a whole number, 0 or more.</p> : null}
          </div>
        </>
      )}
    </section>
  );
}

function DoneCard({ done, task, onAgain }: { done: { session: SessionRow; best: BenchView["personalBest"] }; task?: TaskRow; onAgain: () => void }) {
  const s = done.session;
  return (
    <section className="job done-card" aria-label="Logged">
      <div className="job__status" style={{ justifyContent: "center" }}>Logged · undo for 10 minutes, then it's locked</div>
      <div className="job__target"><span className="job__target-n">{s.actualQty ?? 0}</span><span className="job__target-k">{s.plannedQty ? `of ${s.plannedQty}` : "made"}</span></div>
      {s.task ? <div style={{ marginTop: "var(--space-4)", textAlign: "left" }}>
        <PersonalBest task={s.task} taskName={task?.name} made={s.actualQty} best={done.best && done.best.task === s.task ? done.best : null} />
      </div> : null}
      {isPersonalBest(s.actualQty, done.best && done.best.task === s.task ? done.best : null) ? <p className="small muted" style={{ marginTop: "var(--space-2)" }}>Your own best, against your own history.</p> : null}
      <div className="row" style={{ justifyContent: "center", marginTop: "var(--space-4)" }}>
        <button type="button" className="btn btn--ghost" onClick={async () => { try { await post(paths.undoSession(s.id)); announce(["session"]); onAgain(); } catch { /* shown by the next load */ } }}>Undo</button>
        <button type="button" className="btn" onClick={onAgain}>Back to my bench</button>
      </div>
    </section>
  );
}

function SpareTime({ v, byCode }: { v: BenchView; byCode: Record<string, TaskRow> }) {
  return (
    <section className="job spare" aria-label="Finished early">
      <h2 className="job__title" style={{ fontSize: "var(--text-md)" }}>Finished early? These shelves need it most</h2>
      {v.spareTime.length ? (
        <ul>{v.spareTime.map((s) => (
          <li key={s.shelf}><TaskChip task={s.task} name={byCode[s.task]?.name} group={byCode[s.task]?.group} /> <span className="muted">{s.why}</span></li>
        ))}</ul>
      ) : <p className="small muted">Every shelf you can fill is at or above Recommended. Ask your supervisor what helps.</p>}
      <p className="small muted" style={{ marginTop: "var(--space-2)" }}>Log what you did with "I did some production work".</p>
    </section>
  );
}

function PlanBug({ v, onDone }: { v: BenchView; onDone: () => void }) {
  const [present] = useIonToast();
  const [body, setBody] = useState("");
  const send = async () => {
    try {
      await post(paths.planDoesntWork(), { day: v.now?.day ?? null, half: v.now?.half ?? null, body: body.trim() });
      present({ message: "Sent to the planner as a bug. Thank you.", duration: 3000 });
      announce(["request"]);
      onDone();
    } catch (e) { present({ message: (e as ApiError).message, duration: 3000, color: "danger" }); }
  };
  return (
    <section className="job" aria-label="This plan doesn't work">
      <p className="small" style={{ marginBottom: "var(--space-3)" }}>The plan is the record: if it can't be followed, that's a bug in the planner, not something to work around. Say what's wrong; a supervisor decides.</p>
      <textarea className="textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder="No front covers on the shelf for U17, so I can't start" aria-label="What doesn't work" />
      <button type="button" className="btn btn--primary btn--block" style={{ marginTop: "var(--space-3)" }} disabled={!body.trim()} onClick={send}>Send</button>
    </section>
  );
}

function OtherWork({ v, tasks, onDone }: { v: BenchView; tasks: TaskRow[]; onDone: (s: SessionRow) => void }) {
  const [present] = useIonToast();
  const [half, setHalf] = useState<Half>(v.now?.half ?? "AM");
  const [task, setTask] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      const s = await post<SessionRow>(paths.sessions(), { day: v.now?.day, half, kind: "off_plan", task: task || null, actualQty: Number(qty || 0), note: note.trim() || null, person: v.person ?? undefined } satisfies Partial<LogSessionRequest>);
      onDone(s);
    } catch (e) { present({ message: (e as ApiError).message, duration: 3000, color: "danger" }); }
  };
  return (
    <section className="job" aria-label="Production work not on the plan">
      <div className="field"><span className="field__label">Which half-day</span>
        <div className="choices">{(["AM", "PM"] as Half[]).map((h) => <button key={h} type="button" className="choice" aria-pressed={half === h} onClick={() => setHalf(h)}>{h === "AM" ? "Morning" : "Afternoon"}</button>)}</div></div>
      <label className="field"><span className="field__label">Task (if it was one)</span>
        <select className="select" value={task} onChange={(e) => setTask(e.target.value)}><option value="">Something else</option>{tasks.map((t) => <option key={t.code} value={t.code}>{t.code} {t.name}</option>)}</select></label>
      <label className="field"><span className="field__label">How many</span>
        <input className="input input--num" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} /></label>
      <label className="field"><span className="field__label">What did you do? (needed)</span>
        <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Taped 350 cameras" /></label>
      <button type="button" className="btn btn--primary btn--block" disabled={!note.trim()} onClick={send}>Log it</button>
    </section>
  );
}
